import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import { getPriceForTier } from 'lib/pricingCalculations';
import RemoveButton from './../RemoveButton';
import CategoryTreeModal from './../CategoryTreeModal';
import styles from './CategoryField.scss';

export default class AlgoliaCategoryField extends Component {
  static propTypes = {
    input: PropTypes.object.isRequired,
    inputProps: PropTypes.object,
    meta: PropTypes.object,
    placeholder: PropTypes.string,
    config: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    pricingTiers: ImmutablePropTypes.map.isRequired,
    currentPricingTier: PropTypes.number,
    maxPricingTier: PropTypes.number,
    className: PropTypes.string,
    onBlur: PropTypes.func.isRequired
  };

  state = {
    isModalOpen: false,
  }
  idSalt = Math.random().toString(36).substring(2);

  componentDidMount() {
    if (!window.algoliasearch) { return; }

    this._client = window.algoliasearch(
      this.props.config.get('algolia_app_id'),
      this.props.config.get('algolia_api_key'),
    );

    const EmptyView = ({ query }) => (
      <div className="category-error">
        {`Sorry we can't find a match for "${query}"`}
        <br/><br/>
        {'Try a different search term or '}
        <a onClick={this.toggleModal}>Browse Full Category Tree</a>
      </div>
    );

    const searchSettings = {
      hitsPerPage: 8,
      attributesToHighlight: ['name', 'path_name', 'keywords'],
    };

    let filters = [];
    if (this.props.config.get('premium_edit', false)) {
      filters.push(`pricing_tier_level <= ${this.props.maxPricingTier}`);
    }
    if (this.props.config.get('scope_categories', false)) {
      filters.push(this.props.categories.map(cat => `objectID:${cat.id}`).join(' OR '));
    }
    if (filters.length) {
      searchSettings['filters'] = filters.filter(f => f).map(f => `(${f})`).join(' AND ');
    }

    this._index = this._client.initIndex(`Category_${RAILS_ENV}`);
    this._search = window.autocomplete(`#${this.getControlID()}`, { hints: false }, {
      source: window.autocomplete.sources.hits(this._index, searchSettings),
      displayKey: 'name_with_path',
      templates: {
        suggestion: (suggestion) => {
          const isHigherPlan = this.props.maxPricingTier && suggestion.pricing_tier_level > this.props.maxPricingTier;
          const withinPlan = this.props.maxPricingTier && suggestion.pricing_tier_level <= this.props.maxPricingTier;

          return `
            <div class="${styles.regular} ${isHigherPlan && styles.higherPlan} ${withinPlan && styles.withinPlan}">
              <div class="row">
                <div class="col-6">
                  <span class="text-muted">
                    ${suggestion._highlightResult.path_name.value}
                  </span>
                </div>
              </div>
              <div class="info-row">
                <span class="category-name">${suggestion._highlightResult.name.value}</span>
                <span class="tier-info">
                  ${this.props.pricingTiers.get(suggestion.pricing_tier_level.toString()).get('name')}
                  Plan
                  <span class="tier-pricing">${this.pricing(suggestion)}</span>
                </span>
              </div>
            </div>
          `;
        },
        empty: () => '<div class="category-error"></div>',
      }
    });

    this._search.on('autocomplete:selected', (event, value) => this.onChange(value));
    this._search.on('autocomplete:empty', (e) => {
      ReactDOM.render(<EmptyView query={e.target.value} />, document.querySelector('.category-error'));
    });
    this.setupValue();

    if (this.props.config.get('show_categories', false) && this.props.inputProps.allowModalPreopening) {
      this.toggleModal();
    }
  }

  pricing(suggestion) {
    const { maxPricingTier, currentPricingTier, pricingTiers, config } = this.props;

    if (!maxPricingTier || maxPricingTier === undefined) { return ''; }
    if (suggestion.pricing_tier_level <= currentPricingTier || suggestion.pricing_tier_level <= maxPricingTier) { return ''; }

    const currency = config.get('default_currency');
    const price = parseFloat(getPriceForTier(suggestion.pricing_tier_level, pricingTiers, currency).get('amount', 0));
    if (!price || price === undefined) { return ''; }

    const formattedPrice = Math.ceil(price) === price ? price.toFixed() : price.toFixed(2);

    return `(${currency.get('symbol')}${formattedPrice})`;
  }

  setupValue() {
    if (this.props.input.value) {
      const currentCategory = this.props.categories.find(e => e.id === parseInt(this.props.input.value, 10));
      this._search.autocomplete.setVal(currentCategory.name_with_path);
    } else {
      this._search.autocomplete.setVal('');
    }
  }

  componentDidUpdate() {
    this.setupValue();
  }

  getControlID() {
    return `algolia-search-${this.props.input.name.replace(/[^\w\s]/gi, '')}-${this.idSalt}`;
  }

  onChange = (val) => this.props.input.onChange(parseInt(val.objectID, 10));
  clearSelection = () => this.props.input.onChange(null);
  toggleModal = () => this.setState((previousState) => ({ isModalOpen: !previousState.isModalOpen }));
  onCategorySelect = (val) => {
    const value = parseInt(val, 10);
    this.props.input.onChange(value);
    this.props.onBlur(value);
  }

  render() {
    const { input } = this.props;

    return (
      <div className="aa-input-container">
        <input
          name={input.name}
          className={classnames('form-control', this.props.className)}
          id={this.getControlID()}
          onBlur={() => input.onBlur(input.value)}
          placeholder={this.props.placeholder}
        />
        {this.props.inputProps.clearable && this.props.input.value && <RemoveButton onClick={this.clearSelection} insideForm={true} />}
        {this.state.isModalOpen && <CategoryTreeModal
          isOpen={this.state.isModalOpen}
          categories={this.props.categories}
          onToggle={this.toggleModal}
          onSelect={this.onCategorySelect}
        />}
      </div>
    );
  }
}


// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/components/forms/AlgoliaCategoryField.jsx